import { msalInstance, apiRequest, apiBaseUrl } from '@/auth/msal-config';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Resolved lazily: initAuth() fills apiBaseUrl from /config.json at boot; the
// Vite-baked value stays the fallback (and the api-client tests' msal-config
// mock simply leaves it undefined).
function apiBase(): string {
  return apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '';
}

async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
    return res.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(apiRequest);
    }
    return null;
  }
}

/**
 * Message for a failed admin delete. A 409 means the backend refused because the resource is
 * still bound to segments (EW-192) — the binding has to go first, in its own audited step.
 * The generic sentence is wrong where the refused resource is not itself bindable (a provider
 * is refused because one of its models is bound), so a page can pass its own conflict sentence.
 * The backend's own reason is appended when the body carries one.
 */
export function deleteErrorMessage(error: unknown, conflictText?: string): string {
  if (error instanceof ApiError && error.status === 409) {
    const reason = conflictReason(error.body);
    return (
      (conflictText ??
        'Nem törölhető: az elem szegmensekhez van kötve. Előbb szüntesd meg a kötéseit a Bindings oldalon.') +
      (reason ? `\n\n(${reason})` : '')
    );
  }
  return 'A törlés nem sikerült. Próbáld újra.';
}

/** The backend's own `error` reason from a JSON error body, when it carries one. */
export function conflictReason(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    const reason = (parsed as { error?: unknown } | null)?.error;
    return typeof reason === 'string' ? reason : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;
  readonly code?: string;

  constructor(status: number, statusText: string, body: string) {
    super(`${status} ${statusText}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    try {
      const parsed = JSON.parse(body) as { code?: unknown };
      if (typeof parsed.code === 'string') this.code = parsed.code;
    } catch {
      // Non-JSON body — no code.
    }
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // FormData must keep the browser-generated multipart boundary — never force JSON on it.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${apiBase()}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const token = await getAccessToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`${apiBase()}${path}`, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, body);
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (path: string) => apiFetch<void>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => apiFetch<T>(path, { method: 'POST', body: formData }),
  blob: (path: string) => apiFetchBlob(path),
};
